import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, catchError, filter, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Account, BEAccount } from '../../../interfaces/account';
import { PraticaType, StatoPratica } from '../../../interfaces/pratica';
import { ChannelType } from '../../settings/canals/channel-type';
import { StoreData } from '../models/store-data';
import { Opening } from '../../opening/opening';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly queryEndpoint = `${environment.api}/user-query/user`;
  private readonly commandEndpoint = `${environment.api}/user-command/user`;

  private _me = new BehaviorSubject<StoreData<BEAccount>>(new StoreData());
  me$ = this._me.asObservable();

  private _opening = new BehaviorSubject<StoreData<Opening>>(new StoreData());

  opening$ = this._opening.asObservable();

  constructor(
    public router: Router,
    protected readonly keycloak: KeycloakService,
    private http: HttpClient
  ) {}

  logout() {
    this.keycloak.logout();
  }

  keycloakLogin(routerState: RouterStateSnapshot) {
    let url = window.location.origin + '/';

    return this.keycloak.login({
      redirectUri: url + routerState.url,
    });
  }

  getMe() {
    this._me.next(this._me.value.update('waiting'));
    return this.http.get<BEAccount>(`${this.queryEndpoint}/me`).pipe(
      tap((me) => this._me.next(this._me.value.update('completed', me))),
      //map((me) => me),
      catchError((error) => {
        /* if (error.status === 401) {
          this.router.navigateByUrl('/');
        } */
        this._me.next(this._me.value.update('error'));
        return of(undefined);
      })
    );
  }
  edit(updateAccountData: Partial<Account>, id: string) {
    let body: Partial<Account> = {
      ...updateAccountData,
      id,
    };
    return this.http.patch(this.commandEndpoint + '/' + id, body);
  }

  get me() {
    return Object.freeze(this._me.value.data);
  }

  get isAdmin$() {
    return this.me$.pipe(
      filter((storeData) => storeData.state === 'completed'),
      map((storeData) => {
        let isAdmin = storeData.data!.ruoli.includes('Amministratore');
        let isBO = storeData.data!.ruoli.includes('Back office');
        return isAdmin || isBO;
      })
    );
  }

  get isResponsabile$() {
    return this.me$.pipe(
      filter((storeData) => storeData.state === 'completed'),
      map((storeData) => {
        let isResponsabile = storeData.data!.ruoli.includes('Responsabile');
        return isResponsabile;
      })
    );
  }

  get isResponsabile() {
    let isResponsabile = this.me?.ruoli.includes('Responsabile');
    return isResponsabile || false;
  }

  get isAddetto() {
    let isAddetto = this.me?.ruoli.includes('Addetto');
    return isAddetto || false;
  }

  get isAnalista() {
    let isAnalista = this.me?.ruoli.includes('Analista');
    return isAnalista || false;
  }

  get isDeveloper() {
    let isDeveloper = this.me?.ruoli.includes('Developer');
    return isDeveloper || false;
  }

  get isCoordinatore() {
    let isCoordinatore = this.me?.ruoli.includes('Coordinatore');
    return isCoordinatore || false;
  }

  get isAmministratore() {
    let isAmministratore = this.me?.ruoli.includes('Amministratore');
    return isAmministratore || false;
  }

  get isBackOffice() {
    let isBackOffice = this.me?.ruoli.includes('Back office');
    return isBackOffice || false;
  }

  get isCdcBackOffice() {
    let isBackOffice = this.me?.ruoli.includes('Back office');
    let isCdc = this.me?.groups.includes(ChannelType.CENTROCOMPENTENZA);
    return (isBackOffice && isCdc) || false;
  }

  get isCdc() {
    let isCdc = this.me?.groups.includes(ChannelType.CENTROCOMPENTENZA);
    return isCdc || false;
  }
  get isSpv() {
    let isSpv = this.me?.groups.includes(ChannelType.SPV);
    return isSpv || false;
  }

  get isAml() {
    let isAml = this.me?.groups.includes(ChannelType.CMC);
    return isAml || false;
  }

  get isSupervisore() {
    let isSupervisore = this.me?.ruoli.includes('Supervisore');
    return isSupervisore || false;
  }

  get isPerito() {
    let isPerito = this.me?.ruoli.includes('Perito');
    return isPerito || false;
  }

  get isRiskManager() {
    let isRiskManager = this.me?.ruoli.includes('Risk Manager');
    return isRiskManager || false;
  }

  get isAmministratoreDelegato() {
    let isAmministratoreDelegato = this.me?.ruoli.includes(
      'Amministratore Delegato'
    );
    return isAmministratoreDelegato || false;
  }

  get isOperator() {
    return this.isResponsabile || this.isAddetto;
  }

  getAvailablePraticaStates(praticaType?: PraticaType) {
    let statiPratica: StatoPratica[] | undefined = undefined;
    if (praticaType === 'AML') {
      if (this.isResponsabile || this.isAddetto || this.isCoordinatore) {
        statiPratica = [
          StatoPratica.APERTA,
          StatoPratica.IN_FIRMA,
          StatoPratica.FIRMATA,
        ];
      } else if (this.isBackOffice) {
        statiPratica = [StatoPratica.COMPLETATA];
      } else if (this.isRiskManager) {
        statiPratica = [StatoPratica.IN_VALUTAZIONE_RISK];
      } else if (this.isAmministratoreDelegato) {
        statiPratica = [StatoPratica.IN_VALUTAZIONE_AD];
      }
      return statiPratica;
    }
    if (this.isAmministratore) {
      statiPratica = [
        StatoPratica.NON_CONSENTITA,
        StatoPratica.FIRMATA,
        StatoPratica.RICHIESTA_INTEGRAZIONI,
        StatoPratica.PROPOSTA_MODIFICA,
        StatoPratica.IN_ASSEGNAZIONE,
        StatoPratica.DOCUMENTAZIONE_INTEGRATA,
        StatoPratica.IN_RICHIESTA_DOCUMENTALE,
      ];
    } else if (this.isResponsabile || this.isAddetto) {
      statiPratica = [
        StatoPratica.RICHIESTA_INTEGRAZIONI_CANALE,
        StatoPratica.PROPOSTA_MODIFICA_CANALE,
        StatoPratica.PROPOSTA_DECLINO_CANALE,
        StatoPratica.DELIBERA_MCC,
        StatoPratica.IN_STIPULA,
        StatoPratica.IN_RICHIESTA_DOCUMENTALE,
      ];
    } else if (
      this.isAnalista ||
      this.isBackOffice ||
      (this.isCoordinatore && !this.isCdc)
    ) {
      statiPratica = [
        StatoPratica.IN_VALUTAZIONE_GARANZIA,
        StatoPratica.RISPOSTA_CANALE,
        StatoPratica.IN_VALUTAZIONE_EROGAZIONE,
        StatoPratica.ATTESA_MCC,
        StatoPratica.DELIBERA_MCC,
        StatoPratica.PRENOTATA_STIPULA,
        StatoPratica.IN_STIPULA,
        StatoPratica.STIPULATO,
        StatoPratica.EROGATO,
      ];
    } else if (this.isCoordinatore && this.isCdc) {
      statiPratica = [
        StatoPratica.NON_CONSENTITA,
        StatoPratica.FIRMATA,
        StatoPratica.RICHIESTA_INTEGRAZIONI,
        StatoPratica.PROPOSTA_MODIFICA,
        StatoPratica.IN_ASSEGNAZIONE,
        StatoPratica.DOCUMENTAZIONE_INTEGRATA,
        StatoPratica.IN_RICHIESTA_DOCUMENTALE,
      ];
    } else if (this.isPerito) {
      statiPratica = [StatoPratica.IN_PREVALUTAZIONE];
    }

    return statiPratica;
  }
}
